/* ───────────────────────────────────────────────────────────────────────────
   cf-quote-enquiry-bridge.js — Click Fencing
   2026-08-29

   WHY THIS EXISTS
   On 29 Aug 2026 the first real customer finished a glass quote, pressed
   "Email me this quote instead" on /pages/quote-review, and sent an enquiry
   that contained ONLY this:

       Quote: Frameless Glass Quote 2
       Estimated total: $3,469.22

   No parts list. No drawing. No CRM record — the customer never appeared in
   D1 at all, so there was nothing to look up and nothing to price.

   TWO SEPARATE FAULTS, BOTH FIXED HERE

   1. cf-lead-capture.js's quoteSummary() scraped the breakdown with
        '#qr-summary li, #qr-summary tr, #qr-accordion li'
      The quote-review template renders NEITHER <li> NOR <tr>. It renders
      <div class="qr-summary-card"> and <div class="qr-accordion-row">. So the
      selector matched zero elements, q.lines was empty, and the "Breakdown as
      shown on the site" block was silently omitted. Fixed by reading the SAVED
      QUOTE OBJECT out of localStorage instead of scraping the DOM — the same
      object the checkout path already sends to the CRM, so it cannot drift
      from what the customer saw.

   2. The "Email me this quote instead" route hit Shopify's contact form and
      stopped there. Shopify's contact form emails the store and does nothing
      else — no customer, no order row, no drawing in R2. The "Continue to
      Payment" route, by contrast, POSTs the whole quote to the CRM worker's
      /enquiry endpoint. One route was fully wired and the other was a dead
      end. This bridge sends the SAME /enquiry payload from the contact-form
      route, so both routes now land identically in the CRM.

   WHAT LANDS IN THE CRM NOW
     - customers row (name, email, phone)
     - orders row keyed on a real CF-…-… quote reference, carrying the full
       quote_breakdown and sections JSON
     - the section drawing, as rendered HTML, in the enquiry email
     - an email to enquiries@ with the itemised parts table

   SAFETY
   Every path is wrapped. If anything here throws, the customer's enquiry still
   goes through Shopify's own contact form exactly as it does today — the
   submit is never blocked for longer than SEND_TIMEOUT_MS.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.__cfQuoteEnquiryBridge) return;
  window.__cfQuoteEnquiryBridge = true;

  var CRM_WORKER_URL  = 'https://clickfencing-crm-api.ppfencing2.workers.dev';
  var STASH_KEY       = 'cf_quote_enquiry_payload';
  var SEND_TIMEOUT_MS = 6000;   // never hold the customer's submit longer than this
  /* 2026-10-13 (Pete, David Mogridge's PerfMesh enquiry: "i cant see his drawings"). The plan PDF is built
     HERE on quote-review, where buildQuotePDF and jsPDF live, and filed with the contact-form enquiry. */
  var PDF_STASH_KEY   = 'cf_quote_enquiry_pdf';
  var PDF_BUILD_MS    = 5000;   // never hold the customer on the button longer than this
  var CRM_INTAKE_URL  = 'https://mhzmtafzxeshqqtktbwh.supabase.co/functions/v1/click-quote-intake';
  var CRM_ENQUIRY_RE  = /\/functions\/v1\/click-enquiry\b/;

  /* Same four prefixes quote-review.js uses. Keep in step with CALC_KEYS there. */
  var CALC_KEYS = {
    Frameless: { quotes: 'ullc_quotes',     active: 'ullc_active_quote'     },
    PerfMesh:  { quotes: 'perfmesh_quotes', active: 'perfmesh_active_quote' },
    FlatTop:   { quotes: 'flattop_quotes',  active: 'flattop_active_quote'  },
    Blade:     { quotes: 'blade_quotes',    active: 'blade_active_quote'    }
  };

  function log() {
    try { console.log.apply(console, ['[cf-quote-bridge]'].concat([].slice.call(arguments))); }
    catch (e) { /* ignore */ }
  }

  function money(s) {
    var m = String(s || '').replace(/[, ]/g, '').match(/\$?([0-9]+(?:\.[0-9]{1,2})?)/);
    return m ? parseFloat(m[1]) : 0;
  }

  function fmt(n, cur) {
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency: cur || 'AUD', minimumFractionDigits: 2
      }).format(Number(n) || 0);
    } catch (e) { return '$' + (Number(n) || 0).toFixed(2); }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Find the quote the customer is actually LOOKING AT.

     The page has a "Saved Quotes" pill bar, so the active quote in storage is
     not necessarily the one on screen. Match on the rendered name first, then
     fall back to the rendered total, then to the stored active quote. Reading
     the object rather than the screen is the whole point — the object carries
     the SKUs, quantities and per-section data that the screen only summarises.
     ───────────────────────────────────────────────────────────────────────── */
  function allSavedQuotes() {
    var out = [];
    Object.keys(CALC_KEYS).forEach(function (calcType) {
      try {
        var raw = localStorage.getItem(CALC_KEYS[calcType].quotes);
        if (!raw) return;
        var parsed = JSON.parse(raw);
        var list = Array.isArray(parsed) ? parsed : Object.keys(parsed).map(function (k) { return parsed[k]; });
        list.forEach(function (q) {
          if (q && typeof q === 'object') {
            if (!q.calcType) q.calcType = calcType;
            out.push(q);
          }
        });
      } catch (e) { /* a corrupt key must not stop the others */ }
    });
    return out;
  }

  function findViewedQuote() {
    var quotes = allSavedQuotes();
    if (!quotes.length) return null;

    var nameEl  = document.getElementById('qr-quote-name');
    var totalEl = document.getElementById('qr-grand-total');
    var name    = nameEl ? (nameEl.textContent || '').trim() : '';
    var total   = money(totalEl ? totalEl.textContent : '');

    var byName = quotes.filter(function (q) {
      return name && String(q.quoteName || ('Quote ' + q.quoteNum)).trim() === name;
    });
    // A name can repeat across calculators; the total disambiguates.
    if (byName.length === 1) return byName[0];
    if (byName.length > 1 && total) {
      var exact = byName.filter(function (q) { return Math.abs((Number(q.grandTotal) || 0) - total) < 0.005; });
      if (exact.length) return exact[0];
    }
    if (byName.length) return byName[0];

    if (total) {
      var byTotal = quotes.filter(function (q) { return Math.abs((Number(q.grandTotal) || 0) - total) < 0.005; });
      if (byTotal.length) return byTotal[0];
    }
    return null;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     The readable breakdown — built from the quote object, grouped the way the
     customer saw it. This is what goes into the contact form's message box, so
     the enquiry email is legible even if the CRM call below fails.
     ───────────────────────────────────────────────────────────────────────── */
  function buildBreakdownText(q) {
    var cur   = q.currency || 'AUD';
    var lines = [];
    var pricing = q.pricingType === 'install' ? 'Supply & Install' : 'DIY / Supply Only';

    lines.push('Quote: ' + (q.quoteName || ('Quote ' + q.quoteNum)));
    lines.push('Calculator: ' + (q.calcType || 'Frameless') + '   |   Pricing: ' + pricing);

    var secs = q.sections || [];
    if (secs.length) {
      var totalMm = secs.reduce(function (s, x) { return s + (parseFloat(x.length) || 0); }, 0);
      var gates   = secs.filter(function (x) { return x.hasGate; }).length;
      lines.push('Sections: ' + secs.length + '   |   Total run: ' + (totalMm / 1000).toFixed(2) + ' lm' +
                 (gates ? '   |   Gates: ' + gates : ''));
      lines.push('');
      lines.push('SECTIONS');
      secs.forEach(function (s, i) {
        lines.push('  Section ' + (i + 1) + ': ' + (parseFloat(s.length) || 0) + 'mm' +
                   (s.hasGate ? '  (includes a gate)' : ''));
      });
    }

    var bd = q.breakdown || [];
    if (bd.length) {
      lines.push('');
      lines.push('PARTS LIST');
      var byCat = {};
      var order = [];
      bd.forEach(function (it) {
        var c = it.category || 'Other';
        if (!byCat[c]) { byCat[c] = []; order.push(c); }
        byCat[c].push(it);
      });
      order.forEach(function (cat) {
        lines.push('');
        lines.push('  ' + cat.toUpperCase());
        // Merge the same product across sections, exactly as the CRM email does.
        var merged = {};
        var seq = [];
        byCat[cat].forEach(function (it) {
          var key = it.variantId != null ? 'v:' + it.variantId
                  : it.sku ? 's:' + it.sku
                  : 't:' + it.item + ':' + it.unitPrice;
          if (merged[key]) {
            merged[key].quantity   = (merged[key].quantity || 0) + (it.quantity || 0);
            merged[key].totalPrice = (merged[key].totalPrice || 0) + (it.totalPrice || 0);
          } else {
            merged[key] = { item: it.item, sku: it.sku, quantity: it.quantity,
                            unitPrice: it.unitPrice, totalPrice: it.totalPrice };
            seq.push(key);
          }
        });
        seq.forEach(function (k) {
          var it = merged[k];
          var qty = typeof it.quantity === 'number'
            ? (it.quantity % 1 !== 0 ? it.quantity.toFixed(2) : it.quantity) : '';
          lines.push('    ' + (it.item || '') + (it.sku ? '  [' + it.sku + ']' : '') +
                     '  x' + qty + '  @ ' + fmt(it.unitPrice, cur) + '  = ' + fmt(it.totalPrice, cur));
        });
      });
    }

    lines.push('');
    lines.push('ESTIMATED TOTAL: ' + fmt(q.grandTotal, cur));
    return lines.join('\n');
  }

  /* The section drawing, as it is rendered on the page. The panel graphics are
     <img> tags pointing at absolute Shopify CDN URLs, so this HTML renders in
     an email client unchanged — which is why this is sent instead of trying to
     rasterise a PDF the contact page has no library for. */
  function captureDiagramHtml() {
    try {
      var el = document.getElementById('qr-section-diagrams');
      if (!el) return null;
      var html = el.innerHTML || '';
      if (!html.trim()) return null;
      if (html.length > 120000) return null;      // keep the email sane
      return html;
    } catch (e) { return null; }
  }

  function makeQuoteReference(q) {
    var prefix = String(q.calcType || 'Frameless').slice(0, 4).toUpperCase();
    var rand;
    try { rand = (window.crypto && crypto.randomUUID) ? crypto.randomUUID().slice(0, 8)
                                                      : Math.random().toString(36).slice(2, 10); }
    catch (e) { rand = Math.random().toString(36).slice(2, 10); }
    return 'CF-' + prefix + '-' + (q.quoteNum || Date.now()) + '-' + rand;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     1. /pages/quote-review — take ownership of "Email me this quote instead".

     cf-lead-capture.js adds that button with its own click handler. Rather
     than race it, wait for the button and replace the NODE with a clone —
     cloneNode drops the old listeners — so there is exactly one handler and
     the old DOM-scraping path can never run.
     ───────────────────────────────────────────────────────────────────────── */
  function ownEmailQuoteButton() {
    if (location.pathname.indexOf('/pages/quote-review') === -1) return;

    var tries = 0;
    var iv = setInterval(function () {
      try {
        var btn = document.querySelector('[data-cf-email-quote]');
        if (!btn) { if (++tries > 400) clearInterval(iv); return; }   // ~40s; the page hydrates slowly on cold loads
        if (btn.getAttribute('data-cf-bridged')) { clearInterval(iv); return; }
        clearInterval(iv);

        var fresh = btn.cloneNode(true);
        fresh.setAttribute('data-cf-bridged', '1');
        btn.parentNode.replaceChild(fresh, btn);

        fresh.addEventListener('click', function () {
          try { stashAndGo(); }
          catch (e) {
            log('stash failed, falling through to the plain contact page', e);
            location.href = '/pages/contact#ContactForm';
          }
        });

        log('owned the "Email me this quote instead" button');
      } catch (e) { clearInterval(iv); log('ownEmailQuoteButton failed', e); }
    }, 100);
  }

  function stashAndGo() {
    var q = findViewedQuote();

    if (!q) {
      // Nothing we can attach. Do not invent a payload — just let them through.
      log('no saved quote matched the page; sending the customer on unchanged');
      location.href = '/pages/contact#ContactForm';
      return;
    }

    var breakdownText = buildBreakdownText(q);
    var reference     = makeQuoteReference(q);

    var message = [
      'Please check this quote and send me a fixed price.',
      '',
      breakdownText,
      '',
      'Quote reference: ' + reference,
      '',
      'My questions / anything unusual about the site:',
      ''
    ].join('\n');

    var payload = {
      quote: {
        quoteName:   q.quoteName || ('Quote ' + q.quoteNum),
        quoteNum:    q.quoteNum,
        calcType:    q.calcType || 'Frameless',
        pricingType: q.pricingType || 'diy',
        grandTotal:  Number(q.grandTotal) || 0,
        currency:    q.currency || 'AUD',
        sections:    q.sections || [],
        breakdown:   q.breakdown || [],
        generatedAt: q.generatedAt || new Date().toISOString(),
        diagramHtml: captureDiagramHtml(),
        enquirySource: 'Email me this quote instead'
      },
      quoteReference: reference,
      message: message,
      total: fmt(q.grandTotal, q.currency)
    };

    try { sessionStorage.setItem(STASH_KEY, JSON.stringify(payload)); }
    catch (e) {
      // Almost certainly a quota failure on a very large quote. Drop the
      // drawing (by far the biggest field) and try once more — the parts list
      // matters more than the picture.
      try {
        payload.quote.diagramHtml = null;
        sessionStorage.setItem(STASH_KEY, JSON.stringify(payload));
        log('stashed without the drawing (storage quota)');
      } catch (e2) { log('could not stash the quote at all', e2); }
    }

    // Clear cf-lead-capture's own prefill key so the two cannot both write.
    try { sessionStorage.removeItem('cf_quote_for_enquiry'); } catch (e) { /* ignore */ }

    stashPdfThenGo(q);
  }

  function stashPdfThenGo(q) {
    var gone = false;
    function go() { if (gone) return; gone = true; location.href = '/pages/contact#ContactForm'; }
    try { sessionStorage.removeItem(PDF_STASH_KEY); } catch (e) {}
    try {
      if (typeof window.__cfBuildQuotePDF !== 'function') return go();
      var btn = document.querySelector('[data-cf-email-quote]');
      if (btn) { btn.disabled = true; btn.textContent = 'Preparing your quote\u2026'; }
      setTimeout(go, PDF_BUILD_MS);
      Promise.resolve(window.__cfBuildQuotePDF(q, null)).then(function (built) {
        if (!built || gone) return go();
        return blobToBase64(built.doc.output('blob')).then(function (b64) {
          try {
            sessionStorage.setItem(PDF_STASH_KEY, JSON.stringify({ quoteNum: q.quoteNum, fileName: built.fileName || null, b64: b64 }));
          } catch (e) { log('PDF too big for session storage, sending without it'); }
          go();
        });
      }).catch(function (e) { log('PDF build failed, sending without it', e); go(); });
    } catch (e) { log('stashPdfThenGo failed', e); go(); }
  }

  function blobToBase64(blob) {
    return new Promise(function (resolve, reject) {
      var r = new FileReader();
      r.onloadend = function () { resolve(String(r.result).split(',')[1]); };
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     2. /pages/contact — prefill the full breakdown, then send the real
        enquiry to the CRM when the form is submitted.
     ───────────────────────────────────────────────────────────────────────── */
  function readStash() {
    try {
      var raw = sessionStorage.getItem(STASH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function prefillContact(payload) {
    try {
      var box = document.getElementById('ContactForm-body');
      if (!box) return;
      if (!box.value.trim()) box.value = payload.message;
      try { box.focus(); box.setSelectionRange(box.value.length, box.value.length); } catch (e) {}

      var note = document.createElement('p');
      note.setAttribute('data-cf-quote-note', '1');
      note.textContent = 'Your full quote' + (payload.total ? ' (' + payload.total + ')' : '') +
                         ' — parts list included — has been added to the message below. ' +
                         'Attach your plans or a photo of the pool area if you have them.';
      note.style.cssText = 'font-size:1.35rem;line-height:1.5;margin:0 0 1.2rem;padding:1rem 1.2rem;' +
                           'border-left:3px solid currentColor;opacity:.85;';
      var field = box.closest('.field') || box.parentNode;
      if (field && field.parentNode && !document.querySelector('[data-cf-quote-note]')) {
        field.parentNode.insertBefore(note, field);
      }
      log('full breakdown prefilled into the contact form');
    } catch (e) { log('prefillContact failed', e); }
  }

  function val(id) {
    try { var el = document.getElementById(id); return el ? String(el.value || '').trim() : ''; }
    catch (e) { return ''; }
  }

  function sendEnquiry(payload) {
    var customer = {
      name:     val('ContactForm-name'),
      email:    val('ContactForm-email'),
      phone:    val('ContactForm-phone'),
      address:  '',
      suburb:   '',
      postcode: ''
    };
    var quote = payload.quote;
    quote.customerMessage = val('ContactForm-body');
    quote.fencingInterest = val('ContactForm-interest');

    return fetch(CRM_WORKER_URL + '/enquiry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quote: quote,
        customer: customer,
        quoteReference: payload.quoteReference,
        pdfBase64: null,
        pdfFileName: null
      })
    }).then(function (res) {
      log('enquiry sent to the CRM:', res.status);
      return res.ok;
    });
  }

  /* The page navigates the instant Shopify's form posts, and fetch(keepalive)
     caps the body at 64KB — a full parts list can exceed that. So hold the
     submit, send, then submit for real. Capped at SEND_TIMEOUT_MS so a slow or
     dead worker can never stop a customer from enquiring. */
  function hookContactSubmit(payload) {
    var form = document.querySelector('form[action*="/contact"]');
    if (!form || form.__cfBridgeHooked) return;
    form.__cfBridgeHooked = true;

    var sending = false;

    form.addEventListener('submit', function (ev) {
      try {
        if (sending) return;                                   // the real submit coming back through
        if (!val('ContactForm-name') || !val('ContactForm-email')) return;  // let Shopify validate
        if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

        ev.preventDefault();
        sending = true;

        var btn = form.querySelector('button[type="submit"], input[type="submit"]');
        var btnText = btn ? (btn.textContent || btn.value) : '';
        try { if (btn) { btn.disabled = true; if (btn.textContent) btn.textContent = 'Sending your quote…'; } }
        catch (e) { /* cosmetic only */ }

        var done = false;
        function go() {
          if (done) return;
          done = true;
          try { sessionStorage.removeItem(STASH_KEY); } catch (e) {}
          try { if (btn) { btn.disabled = false; if (btnText && btn.textContent) btn.textContent = btnText; } }
          catch (e) {}
          form.submit();                                       // bypasses listeners; sending guard is belt-and-braces
        }

        setTimeout(go, SEND_TIMEOUT_MS);
        sendEnquiry(payload).then(go, function (err) { log('enquiry send failed', err); go(); });
      } catch (e) {
        log('hookContactSubmit handler failed — letting Shopify take it', e);
      }
    }, false);   // bubble phase, AFTER cf-lead-capture's capture-phase mirror has run

    log('contact submit hooked, quote reference ' + payload.quoteReference);
  }

  /* Since 27 Sep the contact form never fires a submit event (cf-lead-capture.js sends it straight to
     click-enquiry to dodge Shopify's picture puzzles), so hookContactSubmit could no longer run and every
     quote enquiry reached the CRM as text only - no quote row, no PDF. Now: when click-enquiry answers OK,
     the plan the customer sent (the stash, else the saved plan whose total is in the message) is filed on
     the same contact through click-quote-intake with notify:false - click-enquiry has already emailed. */
  function readPdfStash() {
    try { var raw = sessionStorage.getItem(PDF_STASH_KEY); return raw ? JSON.parse(raw) : null; }
    catch (e) { return null; }
  }

  function planForMessage(payload, message) {
    if (payload && payload.quote) return { q: payload.quote, ref: payload.quoteReference };
    var m = String(message || '').match(/estimated total[^$0-9]*\$?\s*([0-9][0-9,]*\.[0-9]{2})/i);
    if (!m) return null;
    var total = money(m[1]);
    var hits = allSavedQuotes().filter(function (q) { return Math.abs((Number(q.grandTotal) || 0) - total) < 0.005; });
    if (!hits.length) return null;
    var q = hits[hits.length - 1];
    return { q: q, ref: q.planRef || makeQuoteReference(q) };
  }

  var origFetch = window.fetch;

  function fileQuoteWithEnquiry(sent, payload) {
    try {
      if (!sent || !(sent.email || sent.phone)) return;
      var hit = planForMessage(payload, sent.message);
      if (!hit) { log('no saved plan matched this enquiry - text only'); return; }
      var q = hit.q;
      var pdf = readPdfStash();
      if (pdf && pdf.quoteNum != null && q.quoteNum != null && pdf.quoteNum !== q.quoteNum) pdf = null;
      var body = {
        customer: { name: sent.name || '', email: sent.email || '', phone: sent.phone || '', suburb: sent.suburb || '' },
        page: 'Click Fencing quote enquiry (contact form)',
        notify: false,
        plans: [{
          planName: q.quoteName || ('Quote ' + q.quoteNum),
          quoteReference: hit.ref || null,
          calculator: q.calcType || null,
          pricingType: q.pricingType || 'diy',
          grandTotal: Number(q.grandTotal) || 0,
          currency: q.currency || 'AUD',
          sections: q.sections || [],
          lines: q.breakdown || [],
          pdfBase64: pdf ? pdf.b64 : null,
          drawingFileName: pdf ? pdf.fileName : null
        }]
      };
      origFetch.call(window, CRM_INTAKE_URL, {
        method: 'POST', mode: 'cors', body: JSON.stringify(body),
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' }
      }).then(function (r) {
        log('plan filed with the enquiry:', r.status, hit.ref, pdf ? '+pdf' : 'no pdf');
        if (r.ok) { try { sessionStorage.removeItem(STASH_KEY); sessionStorage.removeItem(PDF_STASH_KEY); } catch (e) {} }
      }).catch(function (e) { log('plan filing failed', e); });
    } catch (e) { log('fileQuoteWithEnquiry failed', e); }
  }

  function watchEnquirySend(payload) {
    if (window.__cfBridgeEnquiryWatch || typeof window.fetch !== 'function') return;
    window.__cfBridgeEnquiryWatch = true;
    var inner = window.fetch;
    window.fetch = function (input, init) {
      var p = inner.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (CRM_ENQUIRY_RE.test(url) && init && typeof init.body === 'string') {
          var sent = null;
          try { sent = JSON.parse(init.body); } catch (e) {}
          p.then(function (r) { if (r && r.ok) fileQuoteWithEnquiry(sent, payload); }, function () {});
        }
      } catch (e) {}
      return p;
    };
  }

  function onContactPage() {
    if (location.pathname.indexOf('/pages/contact') === -1) return;
    var payload = readStash();
    if (payload && payload.quote) prefillContact(payload);
    watchEnquirySend(payload && payload.quote ? payload : null);
  }

  /* Arrived from a calculator's "Email me this quote" (cf-quote-capture.js sets the flag):
     once the page shows the plan, send it on exactly as the button here would. */
  function sendOnIfFlagged() {
    if (location.pathname.indexOf('/pages/quote-review') === -1) return;
    var at = 0;
    try { at = parseInt(sessionStorage.getItem('cf_send_after_review') || '0', 10); sessionStorage.removeItem('cf_send_after_review'); }
    catch (e) { return; }
    if (!at || Date.now() - at > 120000) return;
    var tries = 0;
    var iv = setInterval(function () {
      try {
        var btn = document.querySelector('[data-cf-email-quote][data-cf-bridged]');
        if (!btn || !findViewedQuote() || typeof window.__cfBuildQuotePDF !== 'function') {
          if (++tries > 150) { clearInterval(iv); location.href = '/pages/contact#ContactForm'; }
          return;
        }
        clearInterval(iv);
        log('sent on from the calculator');
        stashAndGo();
      } catch (e) { clearInterval(iv); log('sendOnIfFlagged failed', e); location.href = '/pages/contact#ContactForm'; }
    }, 100);
  }

  function start() {
    try { ownEmailQuoteButton(); } catch (e) { log('start/quote-review failed', e); }
    try { sendOnIfFlagged(); }    catch (e) { log('start/send-on failed', e); }
    try { onContactPage(); }      catch (e) { log('start/contact failed', e); }
    log('installed');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
